import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Row from '@amzn/meridian/row';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';
import moment from 'moment';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  ConsolidatedContact,
  ContactViewTabObject,
} from '../../../../api/CoachingNotes/schema';
import useCustomTracking from '../../../../Util/useCustomTracking';
import {
  customSort,
  getContactStatusI18nKey,
  NoContactView,
  NOT_FOUND_LABEL,
  SortableColumn,
  SortableDirection,
  TabProps,
} from './TabHelper';
import ExpandableRow from './Expander/ExpandableRow';
import FieldArrayDisplay from '../../../CoachingForm/LayoutComponents/FieldArrayDisplay';
import { convertReviewToDisplay } from './Expander/ExpanderContainer';
import Pagination from '@amzn/meridian/pagination';
import { renderUrl, urls } from '../../../../Util';
import { useNavigate, useParams } from 'react-router';
import PerformancePortalContext from '../../../PerformancePortalContext';

const SavedTab: React.FC<TabProps> = ({
  contacts,
  sourcePage,
}: TabProps): React.ReactElement => {
  const { clearPerformancePortalContextValue } = useContext(
    PerformancePortalContext
  );

  const { userId } = useParams() as { userId: string };
  const navigate = useNavigate();

  const [sortColumn, setSortColumn] = useState(
    SortableColumn.LAST_MODIFIED_DATE
  );
  const [sortDirection, setSortDirection] = useState(
    SortableDirection.DECENDING
  );
  const intl = useIntl();

  // eslint-disable-next-line no-shadow
  const onSort = useCallback(
    ({
      sortColumn,
      sortDirection,
    }: {
      sortColumn: SortableColumn;
      sortDirection: SortableDirection;
    }) => {
      setSortDirection(sortDirection);
      setSortColumn(sortColumn);
    },
    []
  );

  contacts.sort((noteA: ConsolidatedContact, noteB: ConsolidatedContact) =>
    customSort(sortColumn, sortDirection, noteA, noteB)
  );

  const visibleRecordsLen = contacts.length;

  const itemsPerPage = 10;
  const numberOfPages = Math.ceil(contacts.length / itemsPerPage);

  const [currentPage, setCurrentPage] = useState(1);

  const firstVisibleIndex = (currentPage - 1) * itemsPerPage;
  const lastVisibleIndex = firstVisibleIndex + itemsPerPage;

  const [TableSortTrackingTag, tableSortClick] = useCustomTracking(
    'coaching-notes-saved-table-sort',
    'coaching-notes-saved-table-sort',
    {
      'data-csa-c-type': 'table',
      'data-csa-c-sort-column': sortColumn || '',
      'data-csa-c-sort-order': sortDirection || '',
      'data-csa-c-tag1': sourcePage || '',
      'data-csa-c-tag2': 'Contact Table',
      'data-csa-c-tag3': 'SAVED',
    }
  );

  useEffect(() => {
    tableSortClick();
  }, [sortColumn, sortDirection]);

  if (contacts.length === 0) {
    return <NoContactView />;
  }

  return (
    <Column width='100%' alignmentHorizontal='center' spacing='300'>
      <Row width='100%' alignmentHorizontal='start'>
        <Text>
          <FormattedMessage
            id='cs_perfportal_consolidated_saved_not_delivered'
            defaultMessage='Reviewed contacts not yet delivered.'
          />
        </Text>
      </Row>
      <Table
        headerRows={1}
        showDividers
        sortColumn={sortColumn}
        sortDirection={
          sortDirection === SortableDirection.ASCENDING
            ? 'ascending'
            : 'descending'
        }
        onSort={onSort}
        rowComponents={[ExpandableRow, TableRow]}
        layout='fixed'
      >
        <TableSortTrackingTag />
        <TableRow highlightOnHover={false}>
          <TableCell width='11%' sortColumn={SortableColumn.LAST_MODIFIED_DATE}>
            <FormattedMessage
              id='cs_perfportal_coaching_lastUpdate'
              defaultMessage='Last update'
            />
          </TableCell>
          <TableCell width='55%' sortColumn={SortableColumn.CONTACT_ID}>
            <FormattedMessage
              id='PerfPortal_contactId'
              defaultMessage='Contact ID'
            />
          </TableCell>
          <TableCell width='18%' sortColumn={SortableColumn.LAST_MODIFIED_USER}>
            <FormattedMessage
              id='cs_perfportal_coaching_history_coach'
              defaultMessage='Coach'
            />
          </TableCell>
          <TableCell>
            <FormattedMessage
              id='cs_perfportal_status'
              defaultMessage='Status'
              description='Status for Contacts'
            />
          </TableCell>
          <TableCell />
        </TableRow>
        {contacts
          .slice(firstVisibleIndex, lastVisibleIndex)
          .map((contact: ConsolidatedContact, index: number) => {
            const contactStatus =
              contact.contactStatus === null ||
              contact.contactStatus === undefined
                ? contact.contactStatus
                : contact.contactStatus.charAt(0) +
                  contact.contactStatus.slice(1).toLowerCase();

            const isHighlight: boolean = index % 2 !== 0;

            return (
              <ExpandableRow
                key={`${currentPage}_${index}_${contact.contactID}`}
                rowCount={index}
                rowComponents={[ExpandableRow, TableRow]}
                currentPage={currentPage}
                currentSortColumn={sortColumn}
                currentSortDirection={sortDirection}
                isExpandable={true}
              >
                <TableCell data-cy='expandable-cell' width='13%'>
                  {moment(contact.contactInfo?.lastModifiedDate).format(
                    'YYYY-MM-DD'
                  )}
                </TableCell>
                <TableCell width='13%'>{contact.contactID}</TableCell>
                <TableCell width='13%'>
                  <Text>
                    {contact.contactDeliveredUsername ??
                      contact.contactInfo?.lastModifiedUser}
                  </Text>
                </TableCell>

                <TableCell width='13%'>
                  <Text data-cy={`contact-status-${contact.contactID}`}>
                    <FormattedMessage
                      id={getContactStatusI18nKey(contact.contactStatus ?? '')}
                      defaultMessage={contactStatus ?? NOT_FOUND_LABEL}
                    />
                  </Text>
                </TableCell>
                <TableCell>
                  <Link
                    data-csa-c-type='link'
                    data-csa-c-slot-id={`contact-table-form-Edit-action-link-slot`}
                    data-csa-c-content-id={`contact-table-form-Edit-action-link`}
                    data-csa-c-action={`Edit`}
                    data-csa-c-tag1={sourcePage || ''}
                    data-csa-c-tag2='Contact Table'
                    data-csa-c-tag3={ContactViewTabObject.SAVED}
                    data-csa-c-contactid={contact.contactID}
                    data-cy={`contact-table-form-Edit-action-link-${contact.contactID}`}
                    href={renderUrl(
                      `${urls.contactReview}/${userId}/${contact.contactID}`
                    )}
                    onClick={href => {
                      // context is also cleared on top level nav change in PerformancePortalLandingProps
                      // theoretically, this will not do anything, but included here as a backstop incase the user ends up on this page via an alternate path
                      clearPerformancePortalContextValue();
                      navigate(href);
                    }}
                    type='secondary'
                  >
                    <FormattedMessage
                      id='cs_perfportal_coaching_edit'
                      defaultMessage='Edit'
                    />
                  </Link>
                  {/* )} */}
                </TableCell>
                <TableRow
                  backgroundColor={isHighlight ? 'secondary' : 'primary'}
                >
                  <TableCell />
                  <TableCell>
                    <Column>
                      <FieldArrayDisplay
                        mapKey={`contactReview_${currentPage}_${index}_${contact.contactID}`}
                        displayValues={convertReviewToDisplay(contact, intl)}
                      />
                    </Column>
                  </TableCell>
                  <TableCell />
                  <TableCell />
                  <TableCell />
                </TableRow>
              </ExpandableRow>
            );
          })}
      </Table>
      {visibleRecordsLen > 10 ? (
        <Pagination
          showSkipArrows
          numberOfPages={numberOfPages}
          onChange={setCurrentPage}
          currentPage={currentPage}
        />
      ) : null}
    </Column>
  );
};

export default SavedTab;
